import { Card } from "@/components/ui/card";
import { Play, Plus, Pause } from "lucide-react";
import { Track } from "@shared/schema";
import { cn } from "@/lib/utils";
import { useState } from "react";
import { AudioPlayer } from "@/components/ui/audio-player";

interface TrackCardProps {
  track: Track;
  onAddToLicense?: (track: Track) => void;
}

export function TrackCard({ track, onAddToLicense }: TrackCardProps) {
  const [isPlaying, setIsPlaying] = useState(false);
  const [showPlayer, setShowPlayer] = useState(false);

  const handlePlayClick = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    
    if (!showPlayer) {
      setShowPlayer(true);
    }
    
    setIsPlaying(!isPlaying);
  };
  
  const handleAddToLicense = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    
    if (onAddToLicense) {
      onAddToLicense(track);
    }
  };
  
  // Format use case for display
  const formatUseCase = (useCase?: string) => {
    if (!useCase) return 'General Use';
    
    switch(useCase) {
      case 'social_media': return 'Social Media';
      case 'tv': return 'TV';
      case 'film': return 'Film';
      case 'corporate': return 'Corporate';
      default: return useCase.charAt(0).toUpperCase() + useCase.slice(1);
    }
  };

  return (
    <Card className="track-card bg-card text-foreground rounded-xl overflow-hidden shadow-md hover:shadow-lg transition-shadow border-border">
      <div className="relative h-48 bg-black">
        {track.imageUrl && (
          <img 
            src={track.imageUrl}
            alt={`${track.title} artwork`} 
            className="w-full h-full object-cover opacity-80"
          />
        )}
        <div className="absolute inset-0 flex items-center justify-center">
          <button 
            onClick={handlePlayClick}
            className={cn(
              "play-button w-12 h-12 rounded-full flex items-center justify-center transition-opacity",
              isPlaying ? "bg-primary opacity-100" : "bg-primary opacity-70 hover:opacity-100"
            )}
          >
            {isPlaying ? (
              <Pause className="text-black dark:text-white h-5 w-5" />
            ) : (
              <Play className="text-black dark:text-white h-5 w-5 ml-0.5" />
            )}
          </button>
        </div>
        <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black to-transparent p-4">
          <span className="text-xs font-medium text-white bg-primary bg-opacity-90 px-2 py-1 rounded-full">
            {track.genre}
          </span>
        </div>
      </div>
      <div className="p-4">
        <h3 className="font-medium text-lg mb-1 text-foreground">{track.title}</h3>
        <p className="text-muted-foreground text-sm mb-3">
          {track.mood} • {track.duration} • {track.bpm} BPM
        </p>
        
        {showPlayer && (
          <div className="mb-3">
            <AudioPlayer 
              src={track.filePath} 
              trackId={track.id}
              isPlaying={isPlaying}
              onPlayPause={() => setIsPlaying(!isPlaying)}
              onEnded={() => setIsPlaying(false)}
              mini={true}
            />
          </div>
        )}
        
        <div className="flex justify-between items-center">
          <span className="text-xs font-medium text-primary">
            Perfect for {formatUseCase(track.useCase)}
          </span>
          {onAddToLicense && (
            <button 
              onClick={handleAddToLicense}
              className="text-primary hover:text-primary/80"
            >
              <Plus className="h-5 w-5" />
            </button>
          )}
        </div>
      </div>
    </Card>
  );
}
